import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { Pressable, StyleSheet, View } from 'react-native';
import { Fragment, useCallback, useMemo, useRef, useState, } from 'react';
import { Menu } from '@headlessui/react';
import { theme } from '@xbto/design-system';
import { Backdrop, Button, Col, Icon, Row, Surface, TextBody, TextLabel, } from '../../atoms';
import { AppLogo } from '../../molecules/app-logo';
import HUIButtonAdapter from '../../molecules/headlessui-button-adapter';
import { RelationshipManagerCard } from '../relationship-manager-card';
import { useExtraNavCta } from './extra-cta-context';
export default function NavMenu({ floating, menuItems, ActiveTrader, Link, relationshipManager, username, company, }) {
    const buttonRef = useRef(null);
    const handlePress = useCallback((initialOnPress) => {
        return e => {
            var _a, _b;
            initialOnPress === null || initialOnPress === void 0 ? void 0 : initialOnPress(e);
            if (buttonRef.current) {
                // headlessui is not closing the menu on all navigation, we need to
                (_b = (_a = buttonRef.current).click) === null || _b === void 0 ? void 0 : _b.call(_a);
            }
        };
    }, []);
    const { extraNavCta } = useExtraNavCta();
    const NavLink = Link || Fragment;
    /**
     * DOM
     */
    return (_jsx(View, { style: [$.wrapper, floating && $.collapse], children: _jsx(Menu, { style: StyleSheet.flatten([
                $.menuContainer,
                floating && $.collapse,
            ]), as: "div", children: ({ open }) => {
                const navHeader = (_jsxs(Row.LR, { style: $.menuHeader, children: [_jsx(View, { style: $.logoContainer, children: _jsx(NavLink, { to: "/", children: _jsx(AppLogo, {}) }) }), floating && (_jsxs(Row.C, { style: $.menuHeaderCtas, children: [extraNavCta && _jsx(Button, Object.assign({}, extraNavCta)), _jsx(Menu.Button
                                // @ts-expect-error ref is not exposed by headlessui but available
                                , { 
                                    // @ts-expect-error ref is not exposed by headlessui but available
                                    ref: buttonRef, as: HUIButtonAdapter, variant: "tertiary", icon: !open ? 'Menu' : 'Close', size: "sm" })] }))] }));
                return (_jsxs(_Fragment, { children: [open && _jsx(Backdrop, { color: "white" }), floating && !open && navHeader, _jsx(Menu.Items, { style: StyleSheet.flatten([
                                $.menuItems,
                                floating && $.menuItemsFloating,
                            ]), static: !floating, children: _jsxs(_Fragment, { children: [navHeader, menuItems.map(({ title, items }, idx) => (_jsxs(Col, { style: $.menuSection, children: [!!title && (_jsx(TextBody, { color: "tertiary", em: true, lhFull: true, style: $.menuSectionTitle, children: title })), items.map((item, index) => {
                                                return (_jsx(Menu.Item, { children: _jsx(MenuLink, { id: item.id, icon: item.icon, title: item.title, active: item.active, to: item.to, onPress: handlePress(item.onPress), last: index === items.length - 1, Link: Link }) }, item.id));
                                            })] }, title + '' + idx))), _jsx(Menu.Item, { disabled: true, children: _jsxs(View, { style: $.footer, children: [ActiveTrader, !!relationshipManager && (_jsx(RelationshipManagerCard, { name: relationshipManager.name, email: relationshipManager.email, phone: relationshipManager.phone })), (username || company) && (_jsxs(View, { style: $.username, children: [_jsx(TextBody, { em: true, lhFull: true, small: true, children: username }), _jsx(TextLabel, { lhFull: true, children: company })] }))] }) })] }) })] }));
            } }) }));
}
const MenuLink = ({ icon, title, active, to, onPress, last, Link, }) => {
    const [isHovered, setIsHovered] = useState(false);
    const onMouseEnter = useCallback(() => setIsHovered(true), []);
    const onMouseLeave = useCallback(() => setIsHovered(false), []);
    const mouseHandlers = { onMouseEnter, onMouseLeave };
    const content = useMemo(() => {
        return (_jsxs(_Fragment, { children: [_jsx(Icon, { name: icon, color: "primary", size: 16 }), _jsx(TextBody, { color: "primary", em: active, lhFull: true, children: title })] }));
    }, [icon, active, title]);
    const style = useMemo(() => StyleSheet.flatten([
        { padding: 0, borderRadius: theme.radius.sm },
        !last && { marginBottom: theme.spacing['0.5'] },
    ]), [last]);
    let Content;
    if (to && Link) {
        Content = (_jsx(Link, { to: to, style: $.menuLinkContainer, onClick: e => {
                onPress === null || onPress === void 0 ? void 0 : onPress(e);
            }, children: content }));
    }
    else {
        Content = (_jsx(Pressable, { onPress: onPress, style: $.menuLinkContainer, children: content }));
    }
    return (_jsx(Surface, Object.assign({ variant: active ? 'secondary' : isHovered ? 'tertiary' : 'transparent', style: style }, mouseHandlers, { children: Content })));
};
const $ = StyleSheet.create({
    wrapper: {
        height: '100%',
        paddingTop: 0,
        paddingBottom: theme.spacing['4'],
        paddingLeft: theme.spacing['4'],
        paddingRight: theme.spacing['4'],
    },
    menuContainer: {
        flex: 1,
        height: '100vh',
        position: 'relative',
    },
    collapse: { height: 72 /* min height to allow logo+button */ },
    menuHeader: {
        height: 72,
        paddingVertical: theme.spacing['4'],
    },
    menuHeaderCtas: {
        flexDirection: 'row',
        alignItems: 'center',
        columnGap: theme.spacing['2'],
    },
    logoContainer: { paddingVertical: theme.spacing['1.5'] },
    menuSection: {
        marginBottom: theme.spacing['6'],
    },
    menuSectionTitle: {
        marginBottom: theme.spacing['4'],
    },
    menuItems: {
        // @ts-expect-error This is web only
        boxSizing: 'border-box',
        height: '100%',
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
    },
    menuItemsFloating: {
        // @ts-expect-error This is web only
        position: 'fixed',
        top: 0,
        zIndex: 40,
        left: 0,
        right: 0,
        paddingLeft: theme.spacing[4],
        paddingRight: theme.spacing[4],
        paddingBottom: theme.spacing[4],
        backgroundColor: theme.color.background.white,
    },
    menuLinkContainer: {
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        columnGap: theme.spacing['2'],
        padding: theme.spacing['2'],
        // @ts-expect-error This is web only
        textDecoration: 'none',
        textDecorationLine: 'none',
        color: 'inherit',
    },
    footer: {
        rowGap: theme.spacing['4'],
        marginTop: 'auto',
    },
    username: {
        rowGap: theme.spacing['2'],
        marginHorizontal: -theme.spacing['4'],
        paddingTop: theme.spacing['4'],
        paddingHorizontal: theme.spacing['4'],
        borderTopWidth: 1,
        borderTopColor: theme.color.border.primary,
    },
});
